import React, { useState, useEffect, useRef } from 'react';
import './LazyImage.css';

function LazyImage({
  src,
  alt,
  className = '',
  style = {},
  aspectRatio = '16/9',
  onLoad,
  onError,
  onClick,
  ...rest
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    // Optimize: Use native loading="lazy" for better performance
    // Only use Intersection Observer for browsers without native support
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading, show image immediately
      setIsInView(true);
      return;
    }

    // Fallback: Create Intersection Observer for older browsers
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Disconnect observer after image is in view
            if (observerRef.current && imgRef.current) {
              observerRef.current.unobserve(imgRef.current);
            }
          }
        });
      },
      {
        rootMargin: '100px', // Reduced from 200px for lighter loading
        threshold: 0.01
      }
    );

    const currentRef = imgRef.current;
    if (currentRef && observerRef.current) {
      observerRef.current.observe(currentRef);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const handleLoad = (e) => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad(e);
    }
  };

  const handleError = (e) => {
    setHasError(true);
    setIsLoaded(true); // Stop showing shimmer on error
    if (onError) {
      onError(e);
    }
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-wrapper ${className}`}
      style={{
        ...style,
        aspectRatio: aspectRatio,
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onClick}
    >
      {/* Shimmer placeholder */}
      {!isLoaded && !hasError && (
        <div className="lazy-image-shimmer">
          <div className="shimmer-animation"></div>
        </div>
      )}

      {/* Error fallback */}
      {hasError && (
        <div className="lazy-image-error">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
          <span>Image unavailable</span>
        </div>
      )}

      {/* Actual image - only load when in view */}
      {isInView && !hasError && (
        <img
          src={src}
          alt={alt}
          className={`lazy-image ${isLoaded ? 'lazy-image-loaded' : 'lazy-image-loading'}`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
}

export default LazyImage;
